import React, { useState, useEffect } from "react";
import { Box, IconButton, Paper, Drawer, Tooltip } from "@mui/material";
import {
  DoubleArrow,
  Edit,
  FileCopy,
  PowerSettingsNew,
  MotionPhotosAuto,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { tooltipStyleProps } from "services/constants/Styles";
import { themes } from "services/constants";
import { customGetAPI } from "services/utils/CustomAPI";
import { Loader } from "components/Loader";
import { useForm } from "react-hook-form";
import { Preview } from "../Component/Preview";
import { EditShiftCategory } from "../EditShiftCategory";
import { CloneShiftCategory } from "../CloneShiftCategory";
import { customAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";

export const ViewSftCategory = ({
  onClose,
  editCategoryDetails,
  reCallApi,
  setHandleLoader,
  screenAccess,
  onTaskStart,
  onTaskUpdate,
  onTaskComplete,
  setIsViewPanelOpen,
}) => {
  const { setValue, watch } = useForm({
    defaultValues: {
      loading: false,
    },
  });

  const isEmpty = (obj) => !obj || Object.keys(obj).length === 0;
  const { domain } = useSelector((state) => state.tokenReducer);
  const [openSidePeak, setOpenSidePeak] = useState(false);
  const [openCloneSft, setOpenCloneSft] = useState(false);
  const [getViewScreenData, setViewScreenData] = useState(true);
  const [getData, setGetData] = useState([]);
  const [formData, setFormData] = useState({
    basic: {},
    earning: {},
    deductions: {},
  });

  const handleEditShift = () => {
    setOpenSidePeak(true);
  };

  const handleEditClosePanel = () => {
    setOpenSidePeak(false);
  };

  const handleCloneShiftPanel = () => {
    setOpenSidePeak(true);
    setOpenCloneSft(true);
  };

  const handleCloseClonePanel = () => {
    setOpenSidePeak(false);
    setOpenCloneSft(false);
  };

  useEffect(() => {
    const getSftCategoryData = async () => {
      setValue("loading", true);
      const res = await customGetAPI(
        `${domain}administration/shiftroster/shiftrota/shift-categories?shift_category_id=${editCategoryDetails?.id}`,
      );
      if (res.ok) {
        setGetData(res?.responseDetails[0]);
        setValue("loading", false);
      }
    };
    if (getViewScreenData && editCategoryDetails?.id) {
      setViewScreenData(false);
      getSftCategoryData();
    }
  }, [
    domain,
    editCategoryDetails?.id,
    editCategoryDetails?.name,
    getViewScreenData,
    setValue,
    watch,
  ]);

  useEffect(() => {
    if (getData?.length !== 0) {
      const basicCompensation = (
        getData?.compensation_rules?.[0]?.shift_percentage_ranges || [
          { work_min_percentage: "", count_as_shift: "" },
        ]
      ).map(({ work_min_percentage = "", count_as_shift = "" } = {}) => ({
        work_min_percentage,
        count_as_shift,
      }));

      const earnigCompensation = (
        getData?.overtime_policies?.[0]?.overtime_compensation_rules[0]
          ?.ot_percentage_ranges || [
          { work_min_percentage: "", count_as_shift: "" },
        ]
      ).map(({ work_min_percentage = "", count_as_shift = "" } = {}) => ({
        work_min_percentage,
        count_as_shift,
      }));

      setFormData({
        basic: {
          sft_category_name: getData?.name,
          payment_based_on: getData?.payment_based_on ?? "Full Shift Credit",
          rows: basicCompensation,
          additional_percentage:
            getData?.compensation_rules?.[0]?.additional_percentage ?? 0,
          shift_count: getData?.compensation_rules?.[0]?.shift_per_day ?? 0,
          is_include_sft_grade_sfts:
            getData?.other_shift_grade_shifts_choice ?? "Shift Hours",
          sftCategoryName: editCategoryDetails?.name,
          sftCategoryId: editCategoryDetails?.id,
        },
        earning: {
          is_weekoff_paid: getData?.is_paid_week_off,
          is_holiday_paid: getData?.is_paid_holiday,
          is_absence_paid: getData?.is_absence_paid,
          is_overtime_allowed: getData?.ot_allowed,
          //ot
          is_ot_payment_based_on:
            getData?.overtime_policies[0]?.payment_based_on ?? "Attendance",
          ot_timing_privilege:
            getData?.overtime_policies[0]?.ot_time_privilage ?? "Only Before",
          ot_work_preauthorization:
            getData?.overtime_policies[0]?.ot_work_pre_authorization ??
            "Automatic",
          calculation_based_on:
            getData?.overtime_policies[0]?.calculation_based_on ?? "Hours",
          ot_attendance_payment_factor:
            getData?.overtime_policies[0]?.calculation_based_on === "Hours" &&
            getData?.overtime_policies[0]?.payment_based_on === "Attendance"
              ? getData?.overtime_policies?.[0]?.payment_factor
              : 0,
          ot_sft_payment_factor:
            getData?.overtime_policies[0]?.calculation_based_on === "Shift"
              ? getData?.overtime_policies?.[0]?.payment_factor
              : 0,
          is_ot_pay_by_default:
            getData?.overtime_policies?.[0]?.added_payroll_default ?? 0,
          fixed_amount_deduct_type:
            getData?.overtime_policies?.[0]?.payment_based_on === "Amount"
              ? getData?.overtime_policies?.[0]?.consideration
              : "",
          attendance_deduct_type:
            getData?.overtime_policies?.[0]?.payment_based_on === "Attendance"
              ? getData?.overtime_policies?.[0]?.consideration
              : "",
          minimum_ot_consideration:
            getData?.overtime_policies?.[0]?.minimum_ot_consideration_minutes ??
            0,
          additional_ot:
            getData?.overtime_policies?.[0]
              ?.additional_ot_consideration_minutes ?? 0,
          minutes_for_consideration:
            getData?.overtime_policies?.[0]?.minutes_for_consideration ?? 0,
          additional_minutes_for_consideration:
            getData?.overtime_policies?.[0]
              ?.additional_minutes_for_consideration ?? 0,
          ot_fixed_amount_thereshold_limit:
            getData?.overtime_policies?.[0]
              ?.threshold_ot_consideration_minutes ?? 0,
          ot_fixed_amount_payable:
            getData?.overtime_policies?.[0]?.ot_amount_payable ?? 0,
          ot_fixed_amount_additional_mins:
            getData?.overtime_policies?.[0]
              ?.additional_threshold_ot_consideration_minutes ?? 0,
          ot_fixed_amount_additional_payable:
            getData?.overtime_policies?.[0]?.additional_ot_amount_payable ?? 0,
          rows: earnigCompensation,
          deduct_additional_percentage:
            getData?.overtime_policies?.[0]?.overtime_compensation_rules?.[0]
              ?.additional_percentage ?? 0,

          deduct_shift_count:
            getData?.overtime_policies?.[0]?.overtime_compensation_rules?.[0]
              ?.shift_per_day ?? 0,

          //weekoff
          is_payment_for_weekoff_day_attendance:
            getData?.weekoff_category?.payment_for_week_off_attendance ?? false,
          is_weekoff_payment_based_on:
            getData?.weekoff_category?.payment_based_on ?? "Attendance",
          weekoff_attendance_fixed_amount:
            getData?.weekoff_category?.fixed_amount ?? 0,
          weekoff_attendance_payment_factor:
            getData?.weekoff_category?.week_off_payment_factor ?? 0,
          //holiday
          is_payment_for_holiday_day_attendance:
            getData?.holiday_category?.payment_for_holiday_attendance ?? false,
          is_holiday_payment_based_on:
            getData?.holiday_category?.payment_based_on ?? "Attendance",
          holiday_attendance_fixed_amount:
            getData?.holiday_category?.fixed_amount ?? 0,
          holiday_attendance_payment_factor:
            getData?.holiday_category?.holiday_payment_factor ?? 0,
          //absence
          absent_payment_based_on:
            getData?.absent_earnings?.absent_payment_based_on ?? "Paid",
          partial_payment_factor:
            getData?.absent_earnings?.partial_payment_factor ?? 1,
        },
        deductions: {
          deduct_salary_for_absence: getData?.deduct_salary_for_absence,
          is_apply_break_penalty_policy: getData?.break_penalty_deduction,
          is_apply_sft_penalty_policy: getData?.shift_penalty_deduction,
          is_absence_paid_notification: getData?.is_absence_paid,
          //sft penalty policy

          penalty_options:
            getData?.deduction_policies?.[0]?.penalty_based_on ?? "hours",
          dedctions_options:
            getData?.deduction_policies?.[0]?.deduction_based_on ??
            "Attendance",
          attendance_first_mins:
            getData?.deduction_policies?.[0]?.attendance_deductions?.[0]
              ?.first_initial ?? 0,
          attendance_late_exit:
            getData?.deduction_policies?.[0]?.attendance_deductions?.[0]
              ?.early_late ?? 0,

          deductions_attendance_deduct_type:
            getData?.deduction_policies?.[0]?.attendance_deductions?.[0]
              ?.deduction_type ?? "",

          attendance_initial_mins:
            getData?.deduction_policies?.[0]?.attendance_deductions?.[0]
              ?.after_initial ?? 0,

          attendance_additional_mins:
            getData?.deduction_policies?.[0]?.attendance_deductions?.[0]
              ?.additional_early_late ?? 0,

          fixed_amount_early_late:
            getData?.deduction_policies?.[0]?.fixed_amount_deductions?.[0]
              ?.fixed_amount_early_late ?? 0,

          fixed_amount_deduct_amount:
            getData?.deduction_policies?.[0]?.fixed_amount_deductions?.[0]
              ?.fixed_amount_deduction ?? 0,

          deductions_fixed_amount_deduct_type:
            getData?.deduction_policies?.[0]?.fixed_amount_deductions?.[0]
              ?.deduction_type ?? "",

          fixed_amount_extra_mins:
            getData?.deduction_policies?.[0]?.fixed_amount_deductions?.[0]
              ?.fixed_amount_after_initial ?? 0,

          fixed_amount_early_late_deduct_amt:
            getData?.deduction_policies?.[0]?.fixed_amount_deductions?.[0]
              ?.fixed_amount_additional_deduction ?? 0,

          sft_penalty_early_late_mins:
            getData?.deduction_policies?.[0]?.shift_based_deductions?.[0]
              ?.shift_early_late ?? 0,

          sft_penalty_deduct:
            getData?.deduction_policies?.[0]?.shift_based_deductions?.[0]
              ?.shift_deduction ?? 0,

          sft_penalty_additional_mins:
            getData?.deduction_policies?.[0]?.shift_based_deductions?.[0]
              ?.shift_after_initial ?? 0,

          sft_penalty_sft_deduct:
            getData?.deduction_policies?.[0]?.shift_based_deductions?.[0]
              ?.shift_additional_deduction ?? 0,

          sft_penalty_count_thershold:
            getData?.deduction_policies?.[0]?.count_based_deductions?.[0]
              ?.count_early_late ?? 0,

          sft_penalty_count_deduct:
            getData?.deduction_policies?.[0]?.count_based_deductions?.[0]
              ?.count_deduction ?? 0,

          sft_penalty_count_additional_mins:
            getData?.deduction_policies?.[0]?.count_based_deductions?.[0]
              ?.count_after_initial ?? 0,

          sft_penalty_count_sft_deduct:
            getData?.deduction_policies?.[0]?.count_based_deductions?.[0]
              ?.count_additional_deduction ?? 0,

          //absence deductions
          deduction: getData?.absent_deductions?.deduction ?? 0,
          additional_deduction:
            getData?.absent_deductions?.additional_deduction ?? 0,
          extra_deduction: getData?.absent_deductions?.extra_deduction ?? 0,
          extra_additional_deduction:
            getData?.absent_deductions?.extra_additional_deduction ?? 0,
          // break deductions
          break_penalty_deduct_type:
            getData?.break_penalty_policies?.[0]?.consideration ?? "",
          extra_break_minutes:
            getData?.break_penalty_policies?.[0]?.extra_break_minutes ?? 0,
          break_deduction:
            getData?.break_penalty_policies?.[0]?.break_deduction ?? 0,
          additional_break_minutes:
            getData?.break_penalty_policies?.[0]?.additional_break_minutes ?? 0,
          additional_break_deduction:
            getData?.break_penalty_policies?.[0]?.additional_break_deduction ??
            0,
          is_deduct_break_penalty_policy: getData?.break_penalty_policies[0]
            ?.deficit_working_hours
            ? "deficit"
            : "always",
        },
      });
    }
  }, [
    editCategoryDetails?.id,
    editCategoryDetails?.name,
    editCategoryDetails?.status,
    getData?.absent_deductions?.additional_deduction,
    getData?.absent_deductions?.deduction,
    getData?.absent_deductions?.extra_additional_deduction,
    getData?.absent_deductions?.extra_deduction,
    getData?.absent_earnings?.absent_payment_based_on,
    getData?.absent_earnings?.partial_payment_factor,
    getData?.break_penalty_deduction,
    getData?.break_penalty_policies,
    getData?.compensation_rules,
    getData?.deduct_salary_for_absence,
    getData?.deduction_policies,
    getData?.holiday_category?.fixed_amount,
    getData?.holiday_category?.holiday_payment_factor,
    getData?.holiday_category?.payment_based_on,
    getData?.holiday_category?.payment_for_holiday_attendance,
    getData?.is_absence_paid,
    getData?.is_paid_holiday,
    getData?.is_paid_week_off,
    getData?.length,
    getData?.name,
    getData?.ot_allowed,
    getData?.other_shift_grade_shifts,
    getData?.other_shift_grade_shifts_choice,
    getData?.overtime_policies,
    getData?.payment_allowed_partial_shift_work,
    getData?.payment_based_on,
    getData?.shift_penalty_deduction,
    getData?.weekoff_category?.fixed_amount,
    getData?.weekoff_category?.payment_based_on,
    getData?.weekoff_category?.payment_for_week_off_attendance,
    getData?.weekoff_category?.week_off_payment_factor,
  ]);

  const handleStatus = async (status) => {
    // Notify parent about task start
    if (onTaskStart) {
      onTaskStart({
        categoryName: getData?.name,
        message: `${status === "Active" ? "Activating" : "Inactivating"} shift category...`,
        taskType: "status_update",
      });
    }

    const response = await customAPI(
      `${domain}administration/shiftroster/shiftrota/shift-categories/${getData?.id}/`,
      "PUT",
      {
        status: status,
      },
    );

    if (response.ok) {
      showToast(
        "success",
        `Shift category successfully ${status === "Active" ? "Activated" : "Inactivated"}`,
      );

      // Notify parent of completion
      if (onTaskComplete) {
        setTimeout(() => {
          onTaskComplete();
          reCallApi(true);
          onClose();
        }, 1000);
      }
    } else {
      // If failed, notify completion anyway
      if (onTaskComplete) {
        onTaskComplete();
      }
    }
  };

  // ADD: Function to handle task responses
  const handleTaskResponse = (result) => {
    if (result.task_id && result.websocket_url) {
      // Notify parent about task start
      if (onTaskStart) {
        onTaskStart({
          categoryName: editCategoryDetails?.name,
          message: "Starting background processing...",
          taskId: result.task_id,
          taskType: "update",
        });
      }

      // Close the side panel immediately
      setTimeout(() => {
        onClose();
      }, 500);

      // Connect WebSocket for progress updates
      connectTaskWebSocket(result.task_id, result.websocket_url);
    } else {
      // No background task - complete immediately
      if (onTaskComplete) {
        onTaskComplete();
        reCallApi(true);
        onClose();
      }
    }
  };

  // ADD: WebSocket connection function
  const connectTaskWebSocket = (taskId, websocketUrl) => {
    const socket = new WebSocket(websocketUrl);

    socket.onopen = () => {
      console.log("WebSocket connected for task:", taskId);
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("WebSocket progress update:", data);

        // Update parent with progress
        if (onTaskUpdate) {
          onTaskUpdate({
            progress: data.progress || 0,
            message: data.message || "",
            status: data.status || "PROGRESS",
          });
        }

        if (data.status === "SUCCESS") {
          showToast("success", "Background processing completed successfully");

          // Notify parent of completion
          setTimeout(() => {
            if (onTaskComplete) {
              onTaskComplete();
              reCallApi(true);
            }
            socket.close();
          }, 2000);
        } else if (data.status === "FAILURE") {
          showToast(
            "error",
            `Background processing failed: ${data.error || "Unknown error"}`,
          );
          if (onTaskComplete) {
            onTaskComplete();
          }
          socket.close();
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    socket.onerror = (error) => {
      console.error("WebSocket error:", error);
      showToast("warning", "Connection to progress updates interrupted");
    };

    socket.onclose = () => {
      console.log("WebSocket closed");
    };
  };

  return watch("loading") ? (
    <Loader />
  ) : (
    <>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 0,
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ ml: 1 }}>
                  <IconButton
                    onClick={onClose}
                    sx={{
                      color: themes?.grayColor,
                      "&:hover": {
                        backgroundColor: themes?.whiteColor,
                      },
                    }}
                  >
                    <DoubleArrow />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "50%",
                    padding: "4px 4px",
                  }}
                >
                  <Tooltip title="Clone" arrow slotProps={tooltipStyleProps}>
                    <IconButton
                      onClick={() => {
                        if (screenAccess) {
                          handleCloneShiftPanel();
                        } else {
                          showToast(
                            "error",
                            `Access denied: You are not authorized to clone shift category`,
                          );
                        }
                      }}
                      sx={{
                        color: themes?.blueColor,
                        backgroundColor: themes?.primary,
                        borderRadius: "50%",
                        "&:hover": { backgroundColor: themes?.primary },
                      }}
                      disabled={getData?.status === "Inactive"}
                    >
                      <FileCopy />
                    </IconButton>
                  </Tooltip>
                  &nbsp;
                  <Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
                    <IconButton
                      onClick={() => {
                        if (screenAccess) {
                          handleEditShift();
                        } else {
                          showToast(
                            "error",
                            `Access denied: You are not authorized to edit shift category`,
                          );
                        }
                      }}
                      sx={{
                        color: themes?.greenColor,
                        backgroundColor: themes?.primary,
                        borderRadius: "50%",
                        "&:hover": { backgroundColor: themes?.primary },
                      }}
                      disabled={getData?.status === "Inactive"}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>{" "}
                  &nbsp;
                  {getData?.status === "Active" ? (
                    <Tooltip
                      title="Inactive"
                      arrow
                      slotProps={tooltipStyleProps}
                    >
                      <IconButton
                        onClick={() => {
                          if (screenAccess) {
                            handleStatus("Inactive");
                          } else {
                            showToast(
                              "error",
                              `Access denied: You are not authorized to inactivate shift category`,
                            );
                          }
                        }}
                        sx={{
                          color: themes?.redColor,
                          backgroundColor: themes?.primary,
                          borderRadius: "50%",
                          "&:hover": { backgroundColor: themes?.primary },
                        }}
                      >
                        <PowerSettingsNew />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Active" arrow slotProps={tooltipStyleProps}>
                      <IconButton
                        onClick={() => {
                          if (screenAccess) {
                            handleStatus("Active");
                          } else {
                            showToast(
                              "error",
                              `Access denied: You are not authorized to activate shift category`,
                            );
                          }
                        }}
                        sx={{
                          color: themes?.greenColor,
                          backgroundColor: themes?.primary,
                          borderRadius: "50%",
                          "&:hover": { backgroundColor: themes?.primary },
                        }}
                      >
                        <MotionPhotosAuto />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>

            {!isEmpty(formData?.basic) &&
              !isEmpty(formData?.earning) &&
              !isEmpty(formData?.deductions) && (
                <Box>
                  <Preview
                    basicInfoData={formData.basic}
                    earningData={formData.earning}
                    deductionsData={formData.deductions}
                    isPreview={true}
                    onTaskResponse={handleTaskResponse}
                    editCategoryDetails={editCategoryDetails}
                    reCallApi={reCallApi}
                    onClose={onClose}
                  />
                </Box>
              )}
          </Box>
        </Paper>
      </Box>
      <Drawer
        anchor="right"
        open={openSidePeak}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          !openCloneSft ? handleEditClosePanel() : handleCloseClonePanel();
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: "680px",
            maxWidth: "90vw",
            backgroundColor: themes?.whiteColor,
          },
        }}
      >
        {!openCloneSft ? (
          <EditShiftCategory
            basicInfoData={formData?.basic}
            earingData={formData?.earning}
            deductionsData={formData?.deductions}
            closeEditScreen={handleEditClosePanel}
            reCallApi={reCallApi}
            setHandleLoader={setHandleLoader}
            setViewScreenData={setViewScreenData}
            setIsViewPanelOpen={setIsViewPanelOpen}
          />
        ) : (
          <CloneShiftCategory
            basicInfoData={{
              ...formData?.basic,
              sft_category_name: "",
            }}
            earingData={formData?.earning}
            deductionsData={formData?.deductions}
            closeCloneScreen={handleCloseClonePanel}
            reCallApi={reCallApi}
            setHandleLoader={setHandleLoader}
            setViewScreenData={setViewScreenData}
            onClose={onClose}
          />
        )}
      </Drawer>
    </>
  );
};
